import React from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { StatusSelector } from '../../../features/process/components/status-selector';
import { AssigneeSelector } from '../../../components/assignee-selector/assignee-selector';
import { Tooltip } from '../../../components/tooltip/Tooltip';

import { Step, StepStatus } from '../../../app/entities/step';

type StepGanttHeaderProps = {
  step: Step;
  isViewOnly?: boolean;
};

export const StepGanttHeader: React.FC<StepGanttHeaderProps> = observer(({ step, isViewOnly }) => {
  const navigate = useNavigate();
  const handleChangeStatus = async (newStatus: StepStatus) => {
    try {
      await step.update({ status: newStatus });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Container
      id={`${step._id}-header`}
      onClick={() =>
        navigate(`/space/${step.project?.spaceId}/projects/${step.projectId}/steps/${step._id}`)
      }
      data-dim={step.done}
    >
      <TitleContainer>
        <StatusSelector
          onChange={handleChangeStatus}
          statusMap={Step.StatusMapper}
          status={step.status}
          isViewOnly={isViewOnly}
        />

        <Tooltip text="Open step" position="top" delay={500}>
          <Title>{step.displayName}</Title>
        </Tooltip>
      </TitleContainer>

      <div onClick={(e) => e.stopPropagation()}>
        <AssigneeSelector
          isViewOnly={isViewOnly}
          project={step.project!}
          selectedMemberId={step.assigneeId}
          onChange={(assigneeId) => step.update({ assigneeId })}
          iconOnly
        />
      </div>
    </Container>
  );
});

const Container = styled.div`
  position: relative;
  width: 100%;
  border-radius: var(--border-radius-small);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem;
  cursor: pointer;
  background-color: var(--color-surfaces-bg-elevation-3);
  gap: 0.8rem;

  &[data-dim='true'] {
    background-color: var(--color-surfaces-bg-elevation-2);

    & p {
      color: var(--color-texts-middle-contrast);
    }
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 0;
  flex: 1;
  align-items: center;
`;

const Title = styled.p`
  font-weight: 600;
  line-height: 1.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-texts-high-contrast);
`;
